import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

// Lightweight, accessible toast notifications — a replacement for window.alert()
// for transient, non-blocking messages (e.g. "Failed to delete"). Toasts stack
// bottom-right, auto-dismiss after a few seconds, and announce via aria-live so
// screen readers pick them up. Mount <ToastProvider> once near the app root;
// call useToast() anywhere below it.

type ToastKind = "info" | "error" | "success";
interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

/**
 * `sticky` keeps a toast up until it is dismissed (ENG-55775, QA).
 *
 * Five seconds is right for "Saved" and wrong for the report of something
 * IRREVERSIBLE: the post-delete confirmation went before it could be read, and a
 * message nobody read is the same as no message — on the one action in the CMS
 * that cannot be undone from the toast that announces it.
 *
 * Per CALL rather than per kind: `success` covers both "Saved" and "The page is
 * deleted", and it is the consequence that decides, not the colour. Not a longer
 * timeout either — any number is either still too short to read a sentence and
 * act on it, or long enough to be in the way of everything else.
 */
interface ToastOptions {
  sticky?: boolean;
}

interface ToastApi {
  show: (message: string, kind?: ToastKind, opts?: ToastOptions) => void;
  info: (message: string) => void;
  error: (message: string) => void;
  success: (message: string, opts?: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const AUTO_DISMISS_MS = 5_000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message: string, kind: ToastKind = "info", opts?: ToastOptions) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, kind, message }]);
      // A sticky toast schedules nothing — the close button is its only exit, so
      // it cannot be taken away mid-read.
      if (!opts?.sticky) window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      info: (m) => show(m, "info"),
      error: (m) => show(m, "error"),
      success: (m, opts) => show(m, "success", opts),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-stack" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`} role={t.kind === "error" ? "alert" : "status"}>
            <span className="toast-msg">{t.message}</span>
            <button className="toast-close" aria-label="Dismiss" onClick={() => dismiss(t.id)}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
}
